import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export type DailyBriefEvent = {
  id:
    string;

  title:
    string;

  description:
    string |
    null;

  eventType:
    string |
    null;

  startAt:
    string;

  endAt:
    string |
    null;

  location:
    string |
    null;

  status:
    string;

  responsibleUser:
    string |
    null;

  clientId:
    string |
    null;

  clientName:
    string |
    null;

  caseId:
    string |
    null;

  caseTitle:
    string |
    null;

  leadId:
    string |
    null;

  leadName:
    string |
    null;
};

export type DailyBriefData = {
  aggregation: {
    complete: boolean;
    warning?: string;
    failedSources: string[];
  };
  date:
    string;

  generatedAt:
    string;

  schedule:
    DailyBriefEvent[];

  cases: {
    active:
      number;

    waitingClient:
      number;

    waitingCourt:
      number;

    hearingScheduled:
      number;
  };

  communications: {
    inboundToday:
      number;
  };

  followups: Array<{
    id:
      string;

    name:
      string;

    status:
      string;

    priority:
      string |
      null;

    legalProductName:
      string |
      null;

    nextContactAt:
      string;
  }>;

  installments: Array<{
    id:
      string;

    dueDate:
      string |
      null;

    amount:
      number;

    paidAmount:
      number;

    outstanding:
      number;

    status:
      string;

    paymentTitle:
      string;

    clientName:
      string |
      null;

    caseTitle:
      string |
      null;
  }>;
};

export function useDailyBrief(
  date:
    string
) {
  const [
    data,
    setData,
  ] =
    useState<
      DailyBriefData |
      null
    >(
      null
    );

  const [
    loading,
    setLoading,
  ] =
    useState(
      true
    );

  const [
    error,
    setError,
  ] =
    useState<
      string |
      null
    >(
      null
    );

  const load =
    useCallback(
      async () => {
        setLoading(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              `/api/daily-brief?date=${encodeURIComponent(
                date
              )}`
            );

          const payload =
            await response
              .json() as {
              success?:
                boolean;

              error?:
                string;
            } &
              Partial<
                DailyBriefData
              >;

          if (
            !response.ok ||
            !payload.success ||
            !payload.date ||
            !payload.generatedAt ||
              payload.schedule === undefined ||
            !payload.cases ||
            !payload.communications ||
              payload.followups === undefined ||
              payload.installments === undefined
          ) {
            throw new Error(
              payload.error ||
              'Falha ao carregar a visão do dia.'
            );
          }

          const failedSources = new Set(payload.aggregation?.failedSources ?? []);
          setData((previous) => ({
            aggregation: {
              complete: payload.aggregation?.complete !== false,
              warning: payload.aggregation?.warning,
              failedSources: [...failedSources],
            },
            date:
              payload.date,

            generatedAt:
              payload.generatedAt,

            schedule: failedSources.has('schedule') ? previous?.schedule ?? [] : payload.schedule ?? [],

            cases: failedSources.has('cases') ? previous?.cases ?? { active: 0, waitingClient: 0, waitingCourt: 0, hearingScheduled: 0 } : payload.cases,

            communications: failedSources.has('communications') ? previous?.communications ?? { inboundToday: 0 } : payload.communications,

            followups: failedSources.has('followups') ? previous?.followups ?? [] : payload.followups ?? [],

            installments: failedSources.has('financial') ? previous?.installments ?? [] : payload.installments ?? [],
          }));

          setError(
            null
          );
        } catch (
          loadError
        ) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Falha ao carregar a visão do dia.'
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        date,
      ]
    );

  useEffect(
    () => {
      void load();
    },
    [
      load,
    ]
  );

  return {
    data,
    loading,
    error,
    load,
  };
}
